import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { Download, FileText, ShieldCheck, Receipt, Activity, LogOut, Loader2, CreditCard, BookOpen, MessageSquareHeart, Wallet, Zap, LayoutDashboard, TrendingUp, AlertTriangle, KeyRound } from "lucide-react";
import DrillDrawer from "@/components/DrillDrawer";
import { toast } from "sonner";
import PayInvoiceModal from "@/components/PayInvoiceModal";
import CreditsPanel from "@/components/CreditsPanel";
import { TrustedDevicesPanel } from "@/components/TrustedDevicesPanel";
import ClientCommandPalette from "@/components/ClientCommandPalette";
import { fmt as fmtMoney } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLOW = ["received", "accepted", "in_design", "manufacturing", "out_for_delivery", "delivered"];

function StatusBadge({ status, t }) {
    const idx = FLOW.indexOf(status);
    const color = idx >= 5 ? "bg-emerald-500" : idx >= 3 ? "bg-cyan-500" : idx >= 1 ? "bg-amber-500" : "bg-slate-400";
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11.5px] font-medium text-slate-700">
            <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
            {t(`status.${status}`)}
        </span>
    );
}

function StatusTimeline({ history, currentStatus, t }) {
    return (
        <ol className="space-y-3" data-testid="status-timeline">
            {FLOW.map((s, i) => {
                const idx = FLOW.indexOf(currentStatus);
                const reached = i <= idx;
                const event = (history || []).find((h) => h.status === s);
                return (
                    <li key={s} className="grid grid-cols-[20px_1fr_auto] items-center gap-3">
                        <span className={`h-3 w-3 rounded-full ${reached ? "bg-cyan-500" : "bg-slate-200"}`} />
                        <div className={`text-[14px] ${reached ? "text-slate-900 font-medium" : "text-slate-400"}`}>
                            {t(`status.${s}`)}
                        </div>
                        <div className="font-mono-tr text-[11px] tracking-wider text-slate-400">
                            {event?.at ? new Date(event.at).toLocaleString() : ""}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}

export default function Portal() {
    const { user, logout, loading: authLoading } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [cases, setCases] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [creditAccount, setCreditAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCase, setActiveCase] = useState(null);
    const [tab, setTab] = useState("dashboard");
    const [creditBannerDismissed, setCreditBannerDismissed] = useState(false);

    // Deep-link: /portal#credits opens the credits tab
    useEffect(() => {
        if (window.location.hash === "#credits") setTab("credits");
    }, []);
    const [payingInvoice, setPayingInvoice] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Stripe success return handler
    useEffect(() => {
        const sid = searchParams.get("session_id");
        if (!sid) return;
        let attempts = 0;
        const max = 8;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await axios.get(`${API}/payments/stripe/status/${sid}`);
                if (data.payment_status === "paid") {
                    toast.success("Payment received");
                    setSearchParams({}, { replace: true });
                    setTab("invoices");
                    return;
                }
                if (data.status === "expired") {
                    toast.error("Checkout expired");
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch (e) {
                console.warn("payment status poll error (will retry):", e?.message || e);
            }
            if (attempts < max) setTimeout(poll, 2000);
            else setSearchParams({}, { replace: true });
        };
        toast.info("Confirming payment…");
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            nav("/login", { replace: true, state: { from: "/portal" } });
            return;
        }
        if (user.role === "employee") {
            nav("/admin", { replace: true });
            return;
        }
        (async () => {
            try {
                const [c, i, ca] = await Promise.all([
                    axios.get(`${API}/my/cases`),
                    axios.get(`${API}/my/invoices`),
                    axios.get(`${API}/credits/account`).catch(() => ({ data: null })),
                ]);
                setCases(c.data);
                setInvoices(i.data);
                setCreditAccount(ca.data);
                if (c.data.length > 0) setActiveCase(c.data[0]);
            } catch (e) {
                toast.error("Failed to load portal data");
            } finally {
                setLoading(false);
            }
        })();
    }, [user, authLoading, nav]);

    if (authLoading || loading) return (
        <div className="min-h-[60vh] flex items-center justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
    );
    if (!user) return null;

    const downloadWarranty = (caseNumber) => {
        window.open(`${API}/cases/${caseNumber}/warranty.pdf`, "_blank");
    };
    const downloadInvoice = (invoiceNumber) => {
        window.open(`${API}/invoices/${invoiceNumber}/pdf`, "_blank");
    };

    return (
        <div data-testid="portal-page" className="bg-slate-50 min-h-screen">
            <ClientCommandPalette />
            {/* Sub-header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-8 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="tr-overline">· {t("portal.welcome")}</div>
                        <h1 className="mt-2 font-display font-light text-4xl tracking-tight text-slate-900">
                            {user.clinic_name || user.dentist_name}
                        </h1>
                        <div className="mt-1 text-[13.5px] text-slate-500 font-mono-tr tracking-wide">{user.email}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <a
                            href={`${API}/docs/clinic-guide`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="clinic-guide-download"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition-colors text-[12.5px] font-medium"
                            title="Download the clinic onboarding guide as a PDF"
                        >
                            <BookOpen size={13} /> Clinic guide (PDF)
                        </a>
                        <Link to="/workflow#send-case" className="tr-btn-cyan" data-testid="portal-send-case">{t("nav.send_case")}</Link>
                        <button onClick={() => { logout(); nav("/"); }} className="tr-btn-ghost !py-2.5" data-testid="portal-logout">
                            <LogOut size={15} /> {t("nav.logout")}
                        </button>
                    </div>
                </div>
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 flex gap-1 -mb-px">
                    {[
                        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, count: null },
                        { id: "cases", label: t("portal.your_cases"), icon: Activity, count: cases.length },
                        { id: "credits", label: "Credits", icon: Wallet, count: null },
                        { id: "invoices", label: t("portal.your_invoices"), icon: Receipt, count: invoices.length },
                        { id: "warranty", label: t("portal.warranty_cards"), icon: ShieldCheck, count: cases.filter((c) => c.status === "delivered").length },
                        // Iter 74 — security / trusted-devices tab. Lets the
                        // clinic user review which browsers can skip the OTP
                        // and revoke any device they don't recognise.
                        { id: "security", label: "Security", icon: KeyRound, count: null },
                    ].map((tabDef) => {
                        const Icon = tabDef.icon;
                        const active = tab === tabDef.id;
                        return (
                            <button
                                key={tabDef.id}
                                data-testid={`portal-tab-${tabDef.id}`}
                                onClick={() => setTab(tabDef.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-[13.5px] font-medium border-b-2 transition ${
                                    active ? "border-cyan-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                                }`}
                            >
                                <Icon size={15} />
                                {tabDef.label}
                                {tabDef.count !== null && <span className="ml-1 text-[11px] font-mono-tr text-slate-400">({tabDef.count})</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Body */}
            <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-10">
                {/* Credit balance banner — appears when balance is zero/low */}
                {creditAccount && !creditBannerDismissed && tab !== "credits" && (() => {
                    const bal = creditAccount.balance_qar || 0;
                    const lowThreshold = 3000;
                    const isZero = bal <= 0;
                    const isLow = bal > 0 && bal < lowThreshold;
                    if (!isZero && !isLow) return null;
                    return (
                        <div
                            data-testid="credit-balance-banner"
                            className={`mb-6 rounded-2xl border px-5 py-4 flex flex-wrap items-center gap-4 ${
                                isZero
                                    ? "border-rose-200 bg-rose-50/60"
                                    : "border-amber-200 bg-amber-50/60"
                            }`}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isZero ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                    <Wallet size={18} />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-display text-[16px] text-slate-900">
                                        {isZero
                                            ? "Your credit balance is empty"
                                            : `Low credit balance · ${fmtMoney(bal, "QAR")}`}
                                    </div>
                                    <div className="text-[12.5px] text-slate-600">
                                        {isZero
                                            ? "Top up to keep sending cases without delay. Save up to 15% with larger packs."
                                            : `Below ${fmtMoney(lowThreshold, "QAR")}. Enable Auto top-up so you never get blocked mid-case.`}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setTab("credits")}
                                data-testid="credit-banner-topup"
                                className="tr-btn !py-2.5"
                            >
                                <Zap size={15} /> Top up now
                            </button>
                            <button
                                onClick={() => setCreditBannerDismissed(true)}
                                data-testid="credit-banner-dismiss"
                                className="text-[12px] text-slate-500 hover:text-slate-900 px-2 py-1"
                                aria-label="Dismiss"
                            >
                                Dismiss
                            </button>
                        </div>
                    );
                })()}

                {/* Top-of-page payment-due banner */}
                {(() => {
                    const due = invoices.filter((i) => i.status === "due");
                    const totalDue = due.reduce((s, i) => s + (i.total || 0), 0);
                    const cur = (due[0] && due[0].currency) || "QAR";
                    if (due.length === 0) return null;
                    return (
                        <div data-testid="payment-due-banner" className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4 flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700"><CreditCard size={18} /></div>
                                <div>
                                    <div className="font-display text-[16px] text-slate-900">{due.length} unpaid invoice{due.length === 1 ? "" : "s"} · {fmtMoney(totalDue, cur)} due</div>
                                    <div className="text-[12.5px] text-slate-600">Pay online via Stripe or Razorpay — instant confirmation, receipt by email.</div>
                                </div>
                            </div>
                            <button
                                onClick={() => { setTab("invoices"); setPayingInvoice(due[0]); }}
                                data-testid="banner-pay-now"
                                className="tr-btn !py-2.5"
                            >
                                <CreditCard size={15} /> Pay now
                            </button>
                        </div>
                    );
                })()}

                {tab === "dashboard" && (
                    <PortalDashboard
                        cases={cases}
                        creditAccount={creditAccount}
                        onOpenCase={(c) => { setActiveCase(c); setTab("cases"); }}
                        onOpenInvoice={() => setTab("invoices")}
                        onSendCase={() => nav("/workflow#send-case")}
                        onTopUp={() => setTab("credits")}
                    />
                )}

                {tab === "cases" && (
                    <div className="grid lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-5">
                            {cases.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
                                    {t("portal.no_cases")}
                                </div>
                            ) : (
                                <ul className="space-y-2.5" data-testid="cases-list">
                                    {cases.map((c) => (
                                        <li key={c.id}>
                                            <button
                                                onClick={() => setActiveCase(c)}
                                                data-testid={`case-row-${c.case_number}`}
                                                className={`w-full text-left bg-white rounded-xl border p-4 transition ${
                                                    activeCase?.id === c.id ? "border-cyan-300 shadow-md" : "border-slate-200 hover:border-cyan-200"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <div className="font-mono-tr text-[11.5px] text-slate-500 tracking-wider">{c.case_number}</div>
                                                        <div className="font-display text-lg text-slate-900 mt-1 tracking-tight">
                                                            {c.product.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                                        </div>
                                                        <div className="text-[12.5px] text-slate-500 mt-0.5">{t("portal.patient")}: {c.patient_id} · {t("portal.case_number")} {c.shade}</div>
                                                    </div>
                                                    <StatusBadge status={c.status} t={t} />
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {activeCase && (
                            <div className="lg:col-span-7" data-testid="case-detail">
                                <div className="bg-white rounded-2xl border border-slate-200 p-7">
                                    <div className="flex items-start justify-between mb-6">
                                        <div>
                                            <div className="tr-overline">· {t("portal.case_number")}</div>
                                            <div className="font-mono-tr text-2xl text-slate-900 tracking-wider mt-1">{activeCase.case_number}</div>
                                        </div>
                                        <StatusBadge status={activeCase.status} t={t} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mb-7">
                                        <Cell k={t("portal.patient")} v={activeCase.patient_id} />
                                        <Cell k={t("portal.product")} v={activeCase.product.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())} />
                                        <Cell k="Teeth" v={activeCase.tooth_numbers.join(", ")} />
                                        <Cell k="Shade" v={activeCase.shade} />
                                        <Cell k={t("portal.submitted")} v={new Date(activeCase.created_at).toLocaleDateString()} />
                                        <Cell k="Files" v={`${activeCase.files.length} attached`} />
                                    </div>
                                    <div className="mb-7">
                                        <div className="tr-overline mb-4">· Progress</div>
                                        <StatusTimeline history={activeCase.status_history} currentStatus={activeCase.status} t={t} />
                                    </div>
                                    {/* Inline invoice + Pay button for the selected case */}
                                    {(() => {
                                        const inv = invoices.find((i) => i.case_id === activeCase.id);
                                        if (!inv) return null;
                                        const isPaid = inv.status === "paid";
                                        return (
                                            <div data-testid="active-case-invoice" className={`mt-5 rounded-xl border px-4 py-3.5 flex flex-wrap items-center gap-3 ${isPaid ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/40 border-amber-200"}`}>
                                                <Receipt size={18} className={isPaid ? "text-emerald-700" : "text-amber-700"} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[13.5px] text-slate-900 font-medium">
                                                        Invoice <span className="font-mono-tr">{inv.invoice_number}</span> · {fmtMoney(inv.total || 0, inv.currency)}
                                                    </div>
                                                    <div className="text-[11.5px] text-slate-500">
                                                        {isPaid ? "Paid · receipt sent by email." : "Due — pay online via Stripe / Razorpay or by bank transfer."}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => downloadInvoice(inv.invoice_number)}
                                                    data-testid="active-case-invoice-pdf"
                                                    className="tr-btn-ghost !py-2 text-[12px]"
                                                >
                                                    <Download size={13} /> PDF
                                                </button>
                                                {!isPaid && (
                                                    <button
                                                        onClick={() => setPayingInvoice(inv)}
                                                        data-testid="active-case-pay-btn"
                                                        className="tr-btn !py-2 text-[12px]"
                                                    >
                                                        <CreditCard size={13} /> Pay now
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    <div className="flex flex-wrap gap-3 pt-5 border-t border-slate-100">
                                        <Link
                                            to={`/cases/${activeCase.id}`}
                                            data-testid="portal-open-case-detail"
                                            className="tr-btn !py-2.5"
                                        >
                                            <FileText size={15} /> Open full case · files &amp; chat
                                        </Link>
                                        {activeCase.status === "delivered" && (
                                            <button onClick={() => downloadWarranty(activeCase.case_number)} className="tr-btn-ghost !py-2.5" data-testid="download-warranty-btn">
                                                <Download size={15} /> {t("portal.download_warranty")}
                                            </button>
                                        )}
                                        <a href={`${API}/cases/${activeCase.case_number}/warranty.pdf`} target="_blank" rel="noreferrer" className="tr-btn-ghost !py-2.5">
                                            <FileText size={15} /> Preview warranty
                                        </a>
                                        {activeCase.status === "delivered" && (
                                            <Link
                                                to={`/feedback/${activeCase.case_number}`}
                                                data-testid="portal-leave-feedback"
                                                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition text-[13px] font-medium"
                                            >
                                                <MessageSquareHeart size={15} /> Leave feedback
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "credits" && <CreditsPanel />}

                {tab === "invoices" && (
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="invoices-list">
                        {invoices.length === 0 ? (
                            <div className="p-10 text-center text-slate-500">No invoices yet.</div>
                        ) : (
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-slate-50 text-left text-[11px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">
                                        <th className="px-5 py-3">Invoice</th>
                                        <th className="px-5 py-3">Case</th>
                                        <th className="px-5 py-3">Issued</th>
                                        <th className="px-5 py-3">Total</th>
                                        <th className="px-5 py-3">Status</th>
                                        <th className="px-5 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => (
                                        <tr key={inv.id} className="border-t border-slate-100 text-[14px]" data-testid={`invoice-row-${inv.invoice_number}`}>
                                            <td className="px-5 py-4 font-mono-tr text-slate-900">{inv.invoice_number}</td>
                                            <td className="px-5 py-4 font-mono-tr text-slate-600">{inv.case_number}</td>
                                            <td className="px-5 py-4 text-slate-600">{new Date(inv.issued_at).toLocaleDateString()}</td>
                                            <td className="px-5 py-4 font-medium text-slate-900">{fmtMoney(inv.total, inv.currency)}</td>
                                            <td className="px-5 py-4">
                                                <span className={`px-2.5 py-1 rounded-full text-[11.5px] font-medium ${
                                                    inv.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                                                }`}>{inv.status.toUpperCase()}</span>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button onClick={() => downloadInvoice(inv.invoice_number)} className="text-cyan-700 hover:text-cyan-900 text-[13px] font-medium inline-flex items-center gap-1">
                                                    <Download size={14} /> PDF
                                                </button>
                                                <button
                                                    onClick={() => setPayingInvoice(inv)}
                                                    disabled={inv.status === "paid"}
                                                    data-testid={`pay-invoice-${inv.invoice_number}`}
                                                    className={`ml-3 text-[13px] font-medium inline-flex items-center gap-1 ${inv.status === "paid" ? "text-slate-300 cursor-not-allowed" : "text-slate-900 hover:text-cyan-700"}`}
                                                >
                                                    <CreditCard size={13} /> {t("portal.pay_invoice")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "warranty" && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="warranty-list">
                        {cases.filter((c) => c.status === "delivered").length === 0 ? (
                            <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
                                Warranty cards will appear once your cases are delivered.
                            </div>
                        ) : (
                            cases.filter((c) => c.status === "delivered").map((c) => (
                                <div key={c.id} className="rounded-2xl bg-gradient-to-br from-[#0B1120] to-[#1e293b] text-white p-6 relative overflow-hidden tr-grain">
                                    <div className="absolute -top-12 -right-12 w-40 h-40 tr-glow bg-cyan-500/30" />
                                    <div className="relative">
                                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-300">· 10-Year Warranty</div>
                                        <div className="font-display text-3xl tracking-tight mt-2">{c.case_number}</div>
                                        <div className="text-white/60 text-[13px] mt-3">{c.product.replace("-", " ")} · Shade {c.shade}</div>
                                        <div className="text-white/60 text-[13px]">Patient {c.patient_id}</div>
                                        <button onClick={() => downloadWarranty(c.case_number)} className="mt-5 tr-btn-cyan !py-2 !px-3.5 text-[12px]" data-testid={`download-warranty-btn-${c.case_number}`}>
                                            <Download size={13} /> Download PDF
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Iter 74 — Security: trusted-device management for clinic users.
                    Same component the admin Settings page uses, with a portal-
                    flavoured framing. */}
                {tab === "security" && (
                    <div className="max-w-2xl" data-testid="portal-security-tab">
                        <TrustedDevicesPanel
                            title="Trusted devices"
                            subtitle="Browsers + phones that can skip the 6-digit code on sign-in. Revoke any device you don't recognise."
                        />
                    </div>
                )}
            </div>

            {payingInvoice && (
                <PayInvoiceModal
                    invoice={payingInvoice}
                    user={user}
                    onClose={() => setPayingInvoice(null)}
                    onPaid={() => {
                        setPayingInvoice(null);
                        // Refresh invoices
                        axios.get(`${API}/my/invoices`).then(({ data }) => setInvoices(data)).catch(() => {});
                    }}
                />
            )}
        </div>
    );
}

function Cell({ k, v }) {
    return (
        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
            <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">{k}</div>
            <div className="mt-1 text-[14px] text-slate-900 font-medium">{v}</div>
        </div>
    );
}

// --------- Client portal Dashboard (replaces "Cases" as the first view) ---------

function PortalDashboard({ cases, creditAccount, onOpenCase, onOpenInvoice, onSendCase, onTopUp }) {
    const [stats, setStats] = useState(null);
    const [drill, setDrill] = useState(null);
    useEffect(() => {
        let alive = true;
        axios.get(`${API}/my/dashboard`).then((r) => { if (alive) setStats(r.data); }).catch(() => {});
        return () => { alive = false; };
    }, []);
    const recent = (cases || []).slice(0, 5);
    const cur = stats?.invoices?.currency || "QAR";
    const bal = creditAccount?.balance_qar ?? null;

    return (
        <div data-testid="portal-dashboard" className="space-y-6">
            {/* Hero strip */}
            <div className="rounded-2xl bg-gradient-to-br from-[#0B1120] via-[#0f2944] to-[#134e4a] text-white p-6 lg:p-8 relative overflow-hidden">
                <div className="absolute -top-24 -right-10 w-72 h-72 rounded-full bg-cyan-400/20 blur-3xl pointer-events-none" />
                <div className="relative flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-200/80">· Your lab dashboard</div>
                        <h2 className="font-display text-3xl tracking-tight mt-2">Welcome back</h2>
                        <p className="text-[13.5px] text-white/70 mt-1.5 max-w-md">
                            Snapshot of your cases, invoices and credit balance. Click any number to drill into the underlying records.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={onSendCase} data-testid="portal-send-case-cta" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-[#022c33] text-[13px] font-semibold transition">
                            <Activity size={14} /> Send a case
                        </button>
                        {bal !== null && (
                            <button onClick={onTopUp} data-testid="portal-credit-pill" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-[12.5px] border border-white/10 transition">
                                <Wallet size={13} /> Credits: <span className="font-mono-tr">{fmtMoney(bal, "QAR")}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <PStat label="Total orders" value={stats?.cases?.total ?? "—"} icon={Activity} tone="cyan" onClick={() => setDrill({ metric: "cases_total", title: "All your orders" })} testid="pstat-total" />
                <PStat label="In-flight" value={stats?.cases?.in_flight ?? "—"} icon={TrendingUp} tone="indigo" onClick={() => setDrill({ metric: "cases_in_flight", title: "Orders in progress" })} testid="pstat-inflight" />
                <PStat label="Delivered" value={stats?.cases?.delivered ?? "—"} icon={ShieldCheck} tone="emerald" onClick={() => setDrill({ metric: "cases_delivered", title: "Delivered orders" })} testid="pstat-delivered" />
                <PStat label="Late" value={stats?.cases?.late ?? "—"} icon={AlertTriangle} tone={stats?.cases?.late > 0 ? "rose" : "slate"} onClick={() => setDrill({ metric: "cases_late", title: "Late orders", subtitle: "Accepted >24h ago, not delivered" })} testid="pstat-late" />
            </div>

            {/* Invoices row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PStat label="Paid invoices" value={`${stats?.invoices?.paid_count ?? "—"} · ${fmtMoney(stats?.invoices?.paid_amount ?? 0, cur)}`} icon={ShieldCheck} tone="emerald" wide onClick={() => setDrill({ metric: "invoices_paid", title: "Paid invoices" })} testid="pstat-paid" />
                <PStat label="Unpaid invoices" value={`${stats?.invoices?.open_count ?? "—"} · ${fmtMoney(stats?.invoices?.open_amount ?? 0, cur)}`} icon={Receipt} tone={(stats?.invoices?.open_count ?? 0) > 0 ? "amber" : "slate"} wide onClick={() => setDrill({ metric: "invoices_unpaid", title: "Unpaid invoices" })} testid="pstat-unpaid" />
            </div>

            {/* Recent activity */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-[16px] tracking-tight text-slate-900">Most recent cases</div>
                    <button onClick={() => onOpenCase(cases[0])} disabled={cases.length === 0} className="text-[12.5px] text-cyan-700 hover:underline disabled:opacity-40">
                        View all cases →
                    </button>
                </div>
                {recent.length === 0 ? (
                    <div className="py-8 text-center text-[13.5px] text-slate-500">No cases yet. Send your first case above.</div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {recent.map((c) => (
                            <li
                                key={c.id}
                                className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50/50 -mx-2 px-2 rounded-lg transition"
                                onClick={() => onOpenCase(c)}
                                data-testid={`portal-recent-${c.case_number}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-mono-tr text-[12px] text-slate-900">{c.case_number}</div>
                                    <div className="text-[11.5px] text-slate-500 truncate">{c.product || "—"} · {new Date(c.created_at).toLocaleDateString()}</div>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-[10.5px] ${
                                    c.status === "delivered" ? "bg-emerald-50 text-emerald-700"
                                        : c.status === "out_for_delivery" ? "bg-amber-50 text-amber-700"
                                        : "bg-slate-100 text-slate-700"
                                }`}>{(c.status || "").replace(/_/g, " ")}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <DrillDrawer
                open={!!drill}
                onClose={() => setDrill(null)}
                title={drill?.title || ""}
                subtitle={drill?.subtitle}
                endpoint={drill ? `${API}/my/drill?metric=${encodeURIComponent(drill.metric)}` : null}
                currency={cur}
                onPick={(item) => {
                    if (drill?.metric?.startsWith("cases_")) {
                        setDrill(null);
                        onOpenCase(item);
                    } else if (drill?.metric?.startsWith("invoices_")) {
                        setDrill(null);
                        onOpenInvoice();
                    }
                }}
            />
        </div>
    );
}

function PStat({ label, value, icon: Icon, tone = "slate", wide, onClick, testid }) {
    const tones = {
        cyan: "from-cyan-50 to-white border-cyan-100 text-cyan-700",
        emerald: "from-emerald-50 to-white border-emerald-100 text-emerald-700",
        rose: "from-rose-50 to-white border-rose-100 text-rose-700",
        amber: "from-amber-50 to-white border-amber-100 text-amber-700",
        indigo: "from-indigo-50 to-white border-indigo-100 text-indigo-700",
        slate: "from-slate-50 to-white border-slate-200 text-slate-700",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`text-left rounded-2xl border bg-gradient-to-br p-4 transition hover:-translate-y-0.5 hover:shadow-md ${tones[tone] || tones.slate} ${wide ? "" : ""}`}
        >
            <div className="flex items-center justify-between">
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">{label}</div>
                <Icon size={14} className="opacity-70" />
            </div>
            <div className="mt-2 font-display text-2xl tracking-tight text-slate-900">{value}</div>
        </button>
    );
}

